import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  CalendarEvent,
  CalendarEventCategory,
  CalendarSettings,
  CalendarSlotClickEvent,
  CalendarTimeSlot,
  CalendarWeekDay,
  DEFAULT_CALENDAR_SETTINGS,
} from '../../models/calendar.types';
import {
  formatTimeRange,
  generateTimeSlots,
  generateWeekDays,
  isSameDay,
} from '../../utils/calendar-date.utils';

export interface PositionedWeekEvent {
  event: CalendarEvent;
  dayIndex: number;
  topPx: number;
  heightPx: number;
  timeFormatted: string;
}

@Component({
  selector: 'aura-calendar-week-view',
  imports: [MatTooltipModule],
  templateUrl: './calendar-week-view.component.html',
  host: {
    class: 'flex flex-col flex-1 min-h-0 w-full h-full',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CalendarWeekViewComponent {
  readonly currentDate = input.required<Date>();
  readonly events = input<CalendarEvent[]>([]);
  readonly settings = input<CalendarSettings>(DEFAULT_CALENDAR_SETTINGS);

  readonly eventClick = output<CalendarEvent>();
  readonly slotClick = output<CalendarSlotClickEvent>();

  readonly hourHeightPx = 52;

  readonly startHour = computed(() => this.settings().weekStartHour ?? 6);
  readonly endHour = computed(() => this.settings().weekEndHour ?? 22);

  readonly timeSlots = computed<CalendarTimeSlot[]>(() =>
    generateTimeSlots(this.startHour(), this.endHour()),
  );

  readonly weekDays = computed<CalendarWeekDay[]>(() =>
    generateWeekDays(this.currentDate(), this.settings().firstDayOfWeek, this.settings().showWeekends),
  );

  readonly allDayEventsByDay = computed<CalendarEvent[][]>(() => {
    const days = this.weekDays();
    const evts = this.events().filter((e) => e.allDay);
    return days.map((day) => evts.filter((e) => isSameDay(new Date(e.startAt), day.date)));
  });

  readonly hasAnyAllDayEvents = computed<boolean>(() => {
    return this.allDayEventsByDay().some((list) => list.length > 0);
  });

  readonly positionedEvents = computed<PositionedWeekEvent[]>(() => {
    const days = this.weekDays();
    const evts = this.events();
    const startHour = this.startHour();
    const endHour = this.endHour();
    const result: PositionedWeekEvent[] = [];

    for (let dayIndex = 0; dayIndex < days.length; dayIndex++) {
      const day = days[dayIndex];
      const timedDayEvents = evts.filter((e) => !e.allDay && isSameDay(new Date(e.startAt), day.date));

      for (const evt of timedDayEvents) {
        const start = new Date(evt.startAt);
        const end = new Date(evt.endAt);

        const startDec = start.getHours() + start.getMinutes() / 60;
        const endDec = end.getHours() + end.getMinutes() / 60;
        const clampedStart = Math.max(startHour, Math.min(endHour + 1, startDec));
        const clampedEnd = Math.max(clampedStart + 0.5, Math.min(endHour + 1, endDec));

        const duration = clampedEnd - clampedStart;
        const topPx = (clampedStart - startHour) * this.hourHeightPx;
        const heightPx = Math.max(30, duration * this.hourHeightPx - 4);

        result.push({
          event: evt,
          dayIndex,
          topPx,
          heightPx,
          timeFormatted: formatTimeRange(evt.startAt, evt.endAt),
        });
      }
    }

    return result;
  });

  onEventClick(evt: CalendarEvent, event?: Event): void {
    event?.stopPropagation?.();
    this.eventClick.emit(evt);
  }

  readonly currentTimePosition = computed<{ show: boolean; dayIndex: number; topPx: number }>(() => {
    const now = new Date();
    const days = this.weekDays();
    const dayIndex = days.findIndex((d) => d.isToday);
    const startHour = this.startHour();
    const endHour = this.endHour();

    if (dayIndex === -1) {
      return { show: false, dayIndex: -1, topPx: 0 };
    }

    const currentHourDec = now.getHours() + now.getMinutes() / 60;
    if (currentHourDec < startHour || currentHourDec > endHour + 1) {
      return { show: false, dayIndex, topPx: 0 };
    }

    const topPx = (currentHourDec - startHour) * this.hourHeightPx;
    return { show: true, dayIndex, topPx };
  });

  onSlotClicked(day: CalendarWeekDay, hour: number): void {
    const d = new Date(day.date);
    d.setHours(hour, 0, 0, 0);
    this.slotClick.emit({ date: d, hour });
  }

  getEventsForDay(dayIndex: number): PositionedWeekEvent[] {
    return this.positionedEvents().filter((pe) => pe.dayIndex === dayIndex);
  }

  getCategoryStyle(category?: CalendarEventCategory): string {
    switch (category) {
      case 'jade':
        return 'bg-jade-3 text-jade-12 border-jade-7 hover:bg-jade-4';
      case 'amber':
        return 'bg-amber-3 text-amber-12 border-amber-7 hover:bg-amber-4';
      case 'ruby':
        return 'bg-ruby-3 text-ruby-12 border-ruby-7 hover:bg-ruby-4';
      case 'violet':
        return 'bg-violet-3 text-violet-12 border-violet-7 hover:bg-violet-4';
      case 'indigo':
        return 'bg-indigo-3 text-indigo-12 border-indigo-7 hover:bg-indigo-4';
      case 'primary':
      default:
        return 'bg-primary-3 text-primary-12 border-primary-7 hover:bg-primary-4';
    }
  }

  getCategoryBarColor(category?: CalendarEventCategory): string {
    switch (category) {
      case 'jade':
        return 'bg-jade-9';
      case 'amber':
        return 'bg-amber-9';
      case 'ruby':
        return 'bg-ruby-9';
      case 'violet':
        return 'bg-violet-9';
      case 'indigo':
        return 'bg-indigo-9';
      case 'primary':
      default:
        return 'bg-primary-9';
    }
  }
}
